<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoicingNumbering',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
      default: null,
    },
    company: {
      type: Object,
      required: true,
    },
    saving: {
      type: Function,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_3083451129821185_paging = computed(() => false);
  const b_3083451129821185_columns = computed(() => [
    {
      name: 'id',
      header: $fn.value.pgettext('Numbering', 'ID'),
      width: '120px',
      required: true,
    },
    {
      name: 'name',
      header: $fn.value.pgettext('Numbering', 'Name'),
      width: '120px',
      required: true,
    },
    {
      name: 'data_type',
      header: $fn.value.pgettext('Numbering', 'Data type'),
      width: '120px',
      required: true,
    },
    {
      name: 'value',
      header: $fn.value.pgettext('Numbering', 'Value'),
      width: '120px',
      required: true,
    },
  ]);

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3083451129821185_paging: b_3083451129821185_paging.value,
    b_3083451129821185_columns: b_3083451129821185_columns.value,
  }));

  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('read_numbering', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.schemaDefaults(
          'CreateNumbering',
          _.extend(
            {
              start: 1,
              increment: 1,
              variables: [],
            },
            { resource_type: 'invoice:out' }
          )
        );
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3083451129821185_paging', nodeId: '3083451129821185' },
      { name: 'b_3083451129821185_columns', nodeId: '3083451129821185' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: {},
    dataRefs: {
      item,
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3083451129821185_paging,
      b_3083451129821185_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title:
          props.oid == null
            ? $fn.value.gettext('New Numbering')
            : item.value.snapshot('key'),
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.company,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('company');
    }
  );
  watch(
    () => props.saving,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('saving');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_rk31e8589_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_script_fv8exwth6_value_callable = async (
    $event
  ) => {
    return props
      .saving(item.value.getData())
      .then((result) =>
        $imodal.value.close(props.modalName, { ok: true, value: result })
      );
  };
  const act_1040167445267876_script_fv8exwth6_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_fv8exwth6_value_callable($event);
      },
    };
  };
  const act_2434514588044777_closeModal_oujgx4v50_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_rk31e8589 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_rk31e8589_getActionArgs,
    });
  const inst_evh_7315092382398562_close_d3aypfucp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_rk31e8589,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_d3aypfucp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_q6tfjdh1l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i1nfu5dqf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_q6tfjdh1l,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i1nfu5dqf],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_a7p4g115y =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_5nmg21byn =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_a7p4g115y,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_5nmg21byn],
    event: 'close',
  });
  const act_1040167445267876_script_fv8exwth6 = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_fv8exwth6_getActionArgs,
    displayName: 'submit',
  });
  const inst_evh_1040167445267876_submit_zgouwarpq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_fv8exwth6,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_zgouwarpq],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_oujgx4v50 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_oujgx4v50_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_h28ln4k62 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_oujgx4v50,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_h28ln4k62],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_d3aypfucp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_i1nfu5dqf,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_5nmg21byn,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_submit_zgouwarpq,
    submit: inst_evh_1040167445267876_submit_zgouwarpq,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_h28ln4k62,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_d3aypfucp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_d3aypfucp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_i1nfu5dqf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i1nfu5dqf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_5nmg21byn = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_5nmg21byn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_zgouwarpq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_zgouwarpq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_h28ln4k62 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_h28ln4k62.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoicingNumbering"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="numbering"
        :readonly="item.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              name="format"
              :label="pgettext('Numbering', 'Format')"
              :label-cols-md="2"
              :content-cols-md="8"
              required
            >
              <ItpFormInput
                name="format"
                v-model="item.format"
                :value="item.format"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.format =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="start"
              :label="pgettext('Numbering', 'Start')"
              :label-cols-md="2"
              :content-cols-md="2"
              required
            >
              <ItpFormInput
                type="number"
                name="start"
                v-model="item.start"
                :value="item.start"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.start =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="increment"
              :label="pgettext('Numbering', 'Increment')"
              :label-cols-md="2"
              :content-cols-md="2"
              required
            >
              <ItpFormInput
                type="number"
                name="increment"
                v-model="item.increment"
                :value="item.increment"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.increment =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr />
      <ItpDataTable
        :paging="b_3083451129821185_paging"
        :value="item.variables"
        :columns="b_3083451129821185_columns"
        v-if="loaded"
      >
      </ItpDataTable>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.numbering && forms.numbering.id"
          :text="pgettext('Numbering', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms.numbering || !forms.numbering.submittable"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Numbering', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
